import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import type { CreateNoteItemViewModel } from './models'
import type { CreateItemProps } from './utils'
import { createNotePayloadFromViewModel } from './utils'
import { formOptions } from '../Common/Fields/config'
import NoteFields from '../Common/NoteFields'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { scrollToNewItemWithRetry } from 'tripPlanner/utils/scrollToNewItem'

function CreateNote(props: CreateItemProps) {
  const { tripId, defaultDate, rankSpec } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()
  const methods = useForm<CreateNoteItemViewModel>({
    ...formOptions,
    defaultValues: {
      type: 'NOTE',
      sourceType: 'NA',
      startDate: defaultDate,
      isSectionHeader: false,
    },
  })
  const { handleSubmit } = methods

  const { mutate: createMutate, isLoading } = useCreateTripItem({
    onSuccess: (res) => {
      scrollToNewItemWithRetry(res.id)
      closeModal()
    },
  })

  const createNoteItem = useCallback(
    (viewModel: CreateNoteItemViewModel) => {
      createMutate({
        tripId,
        tripItem: createNotePayloadFromViewModel(viewModel),
        rankSpec,
      })
    },
    [createMutate, tripId, rankSpec],
  )

  const modalTitle = intl.formatMessage({
    id: '0hRKnQR7bg-wijSoVcK9',
    defaultMessage: 'Add note',
    description: 'Modal title when adding a note to trip in the trip planner feature',
  })
  const saveLabel = intl.formatMessage({
    id: '5GmZJ4yDhGj5o6PZWgEB',
    defaultMessage: 'Save',
    description: 'Save button label in the add note modal in the trip planner item editor modal',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <FormProvider {...methods}>
        <ModalBody>
          <ModalContent>
            <NoteFields tripId={tripId} />
          </ModalContent>
        </ModalBody>

        <ModalFooter
          primaryActionProps={{
            onClick: handleSubmit(createNoteItem),
            children: saveLabel,
            disabled: isLoading,
          }}
        />
      </FormProvider>
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default CreateNote
